import React from 'react';

import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const BlogGrid = ({ posts, loading }) => {
  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'beginner':
        return 'bg-success/10 text-success border-success/20';
      case 'intermediate':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'advanced':
        return 'bg-error/10 text-error border-error/20';
      default:
        return 'bg-accent/10 text-accent border-accent/20';
    }
  };

  const getCategoryColor = (category) => {
    switch (category?.toLowerCase()) {
      case 'technical tutorials':
        return 'bg-accent/10 text-accent border-accent/20';
      case 'career growth':
        return 'bg-success/10 text-success border-success/20';
      case 'community building':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'industry insights':
        return 'bg-error/10 text-error border-error/20';
      default:
        return 'bg-muted text-text-secondary border-border';
    }
  };

  if (loading) {
    return (
      <section className="bg-background py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)]?.map((_, index) => (
              <div key={index} className="bg-card rounded-xl border border-border overflow-hidden animate-pulse">
                <div className="h-48 bg-muted" />
                <div className="p-6 space-y-4">
                  <div className="flex space-x-2">
                    <div className="h-6 w-20 bg-muted rounded-full" />
                    <div className="h-6 w-16 bg-muted rounded-full" />
                  </div>
                  <div className="space-y-2">
                    <div className="h-6 bg-muted rounded w-full" />
                    <div className="h-6 bg-muted rounded w-3/4" />
                  </div>
                  <div className="space-y-2">
                    <div className="h-4 bg-muted rounded w-full" />
                    <div className="h-4 bg-muted rounded w-5/6" />
                    <div className="h-4 bg-muted rounded w-2/3" />
                  </div>
                  <div className="flex justify-between items-center pt-4">
                    <div className="flex space-x-4">
                      <div className="h-4 w-12 bg-muted rounded" />
                      <div className="h-4 w-12 bg-muted rounded" />
                    </div>
                    <div className="h-8 w-20 bg-muted rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!posts || posts?.length === 0) {
    return (
      <section className="bg-background py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 bg-muted rounded-full flex items-center justify-center">
              <Icon name="Search" size={32} className="text-text-secondary" />
            </div>
            <h3 className="text-xl font-brand-subheading text-foreground mb-2">
              No articles found
            </h3>
            <p className="text-text-secondary font-brand-body mb-6">
              Try adjusting your search criteria or browse all categories.
            </p>
            <Button
              variant="outline"
              iconName="RotateCcw"
              iconPosition="left"
              iconSize={16}
            >
              Reset Filters
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-background py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts?.map((post) => (
            <article
              key={post?.id}
              className="bg-card rounded-xl border border-border overflow-hidden hover:brand-shadow-lg transition-all duration-300 group"
            >
              {/* Post Image */}
              <div className="relative h-48 overflow-hidden">
                <Image
                  src={post?.image}
                  alt={post?.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                
                {/* Image Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                {/* Featured Badge */}
                {post?.featured && (
                  <div className="absolute top-4 left-4">
                    <span className="flex items-center space-x-1 px-2 py-1 bg-primary/90 backdrop-blur-sm text-primary-foreground rounded-full text-xs font-brand-medium">
                      <Icon name="Star" size={12} />
                      <span>Featured</span>
                    </span>
                  </div>
                )}

                {/* Reading Time */}
                <div className="absolute top-4 right-4">
                  <span className="flex items-center space-x-1 px-2 py-1 bg-secondary/80 backdrop-blur-sm text-white rounded-full text-xs font-brand-medium">
                    <Icon name="Clock" size={12} />
                    <span>{post?.readTime}</span>
                  </span>
                </div>
              </div>

              {/* Post Content */}
              <div className="p-6 space-y-4">
                {/* Post Meta */}
                <div className="flex flex-wrap items-center gap-2">
                  {post?.difficulty && (
                    <span className={`px-2 py-1 rounded-full text-xs font-brand-medium border ${getDifficultyColor(post?.difficulty)}`}>
                      {post?.difficulty}
                    </span>
                  )}
                  <span className={`px-2 py-1 rounded-full text-xs font-brand-medium border ${getCategoryColor(post?.category)}`}>
                    {post?.category}
                  </span>
                </div>

                {/* Post Title */}
                <h3 className="text-lg font-brand-subheading text-foreground leading-tight group-hover:text-primary transition-brand line-clamp-2">
                  {post?.title}
                </h3>

                {/* Post Excerpt */}
                <p className="text-text-secondary font-brand-body text-sm line-clamp-3">
                  {post?.excerpt}
                </p>

                {/* Post Tags */}
                {post?.tags && post?.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {post?.tags?.slice(0, 3)?.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-muted text-text-secondary rounded-md text-xs font-brand-medium hover:bg-primary/10 hover:text-primary transition-brand cursor-pointer"
                      >
                        #{tag}
                      </span>
                    ))}
                    {post?.tags?.length > 3 && (
                      <span className="px-2 py-1 text-text-secondary text-xs font-brand-medium">
                        +{post?.tags?.length - 3} more
                      </span>
                    )}
                  </div>
                )}

                {/* Post Footer */}
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="flex items-center space-x-4 text-xs text-text-secondary">
                    <div className="flex items-center space-x-1">
                      <Icon name="Calendar" size={12} />
                      <span>{formatDate(post?.publishedAt)}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-1">
                        <Icon name="Eye" size={12} />
                        <span>{post?.views > 1000 ? `${(post?.views / 1000)?.toFixed(1)}k` : post?.views}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="Heart" size={12} />
                        <span>{post?.likes}</span>
                      </div>
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="xs"
                    iconName="ArrowRight"
                    iconPosition="right"
                    iconSize={14}
                    className="text-text-secondary hover:text-primary group-hover:translate-x-1 transition-all duration-300"
                  >
                    Read
                  </Button>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Load More */}
        <div className="text-center mt-12">
          <Button
            variant="outline"
            size="lg"
            iconName="ChevronDown"
            iconPosition="right"
            iconSize={18}
            className="border-primary/20 text-primary hover:bg-primary/10"
          >
            Load More Articles
          </Button>
        </div>
      </div>
    </section>
  );
};

export default BlogGrid;